import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PageContentRow {
  id: string;
  slug: string;
  page_title: string;
  content: Record<string, any>;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export const usePageContent = <T extends Record<string, any>>(
  slug: string,
  fallbackContent: T
) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["page-content", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      if (error) throw error;
      return data as unknown as PageContentRow | null;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Merge: CMS content overrides fallback, fallback fills gaps
  const rawContent = data?.content || {};
  const { _seo, ...pageContent } = rawContent as Record<string, any>;
  const content: T = data?.content
    ? { ...fallbackContent, ...(pageContent as T) }
    : fallbackContent;

  // SEO data stored inside content JSONB under _seo key
  const seo = _seo && Object.keys(_seo).length > 0 ? _seo : null;

  return { content, seo, isLoading, error };
};

/**
 * Preview-aware version: fetches draft/review content for admin preview.
 */
export const usePreviewContent = <T extends Record<string, any>>(
  slug: string,
  fallbackContent: T,
  previewMode: boolean
) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["page-content-preview", slug, previewMode],
    queryFn: async () => {
      let query = supabase
        .from("page_content")
        .select("*")
        .eq("slug", slug);

      if (!previewMode) {
        query = query.eq("status", "published");
      }

      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return data as unknown as PageContentRow | null;
    },
    staleTime: previewMode ? 0 : 5 * 60 * 1000,
    retry: 1,
  });

  const rawContent = data?.content || {};
  const { _seo, ...pageContent } = rawContent as Record<string, any>;
  const content: T = data?.content
    ? { ...fallbackContent, ...(pageContent as T) }
    : fallbackContent;

  const seo = _seo && Object.keys(_seo).length > 0 ? _seo : null;
  const status = data?.status || "published";

  return { content, seo, status, isLoading, error };
};
